/**
 * A screen that constitutes the core of the "join" workflow, which involves
 * entering one or more class codes to quickly join a teacher's class.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {markMinorConversions} from "@repo/analytics-learning-time";
import type {UpdateStep, UserClass} from "@repo/auth-shared";
import {validateClassCode} from "@repo/auth-signup";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {PermissionsInfo} from "@repo/shared-core";
import {standardTransitionExtras} from "@repo/shared-styles";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import ClassCodeInput from "#districts-package/join-page/class-code-input.tsx";
import ProgressShellActionScreen from "#shared/auth/progress-shell/progress-shell-action-screen.tsx";

import type {
    ClassInfo,
    ClassInfoVariables,
} from "./__graphql-types__/ClassInfo.ts";

// Specific strings used to track the status in state for an input that has
// a delay after submitting.
type SubmitStatus = "ready" | "inProgress" | "finished";

// The length of the animation for removing the login button from the page,
// in seconds.
const TRANSITION_LENGTH = 0.4;

// `includesCentrallyOwnedDistrictData` is used to determine if the user should be
// redirected to the "/join/districts" page
const CLASS_INFO_QUERY: GqlOperation<ClassInfo, ClassInfoVariables> = gqlOp(gql`
    query ClassInfo($classCode: String!) {
        joinByClassCodeInfo(classCode: $classCode) {
            id
            cacheId
            name
            signupCode
            includesCentrallyOwnedDistrictData
        }
    }
`);

type OwnProps = {
    onClassChange: (userClass: UserClass) => void;
    under13: boolean;
    updateStep: UpdateStep;
};

type Props = OwnProps & WithApolloProps;

type State = {
    codeError: string | null | undefined;
    submitStatus: SubmitStatus;
    classCode: string;
};

class JoinWidgetActionScreen extends React.Component<Props, State> {
    state: State = {
        codeError: null,
        submitStatus: "ready",
        classCode: "",
    };

    handleCodeSubmission: (code: string) => void = (code: string) => {
        this.setState({submitStatus: "inProgress"});

        const variables = {classCode: code} as const;

        this.props.gqlClient
            .query(CLASS_INFO_QUERY, {
                variables,
                fetchPolicy: "network-only",
            })
            .then((response) => {
                const studentList = response.data?.joinByClassCodeInfo;
                if (!response.data || !studentList) {
                    markMinorConversions([
                        {
                            id: "activation_2.0_join_code_click_continue",
                            extra: {
                                classCode: code,
                                codeError: "invalidCode",
                            },
                        },
                    ]);
                    this.setState({
                        codeError: "invalidCode",
                        submitStatus: "ready",
                    });
                    return;
                }
                this.setState({
                    codeError: null,
                    submitStatus: "finished",
                });

                markMinorConversions([
                    {
                        id: "activation_2.0_join_code_click_continue",
                        extra: {
                            classCode: code,
                        },
                    },
                ]);
                // NOTE (josh): a `studentList` is just a class.
                // It's the server-side terminology for classes.
                this.addItem({
                    name: studentList.name,
                    signupCode: code,
                    includesCentrallyOwnedDistrictData:
                        studentList.includesCentrallyOwnedDistrictData,
                });
            })
            .catch((err) => {
                markMinorConversions([
                    {
                        id: "activation_2.0_join_code_click_continue",
                        extra: {
                            classCode: code,
                            codeError: "invalidCode",
                        },
                    },
                ]);
                this.setState({
                    codeError: "invalidCode",
                    submitStatus: "ready",
                });
            });
    };

    addItem: (userClass: UserClass) => void = (userClass: UserClass) => {
        const {onClassChange, updateStep} = this.props;
        onClassChange(userClass);

        updateStep("confirmClass");
    };

    handleClassCodeChange: (classCode: string) => void = (
        classCode: string,
    ) => {
        const err = validateClassCode(classCode);
        if (err !== null) {
            this.setState({
                codeError: err,
            });
        } else {
            this.setState({
                classCode,
                codeError: "",
            });
        }
    };

    renderLargeClassCodeInput: () => React.ReactNode = () => {
        const {classCode, codeError} = this.state;
        return (
            <View>
                <HeadingMedium>{t`Enter your class code`}</HeadingMedium>
                <Strut size={spacing.xLarge_32} />
                <ClassCodeInput
                    onClassCodeChange={this.handleClassCodeChange}
                />
                {codeError ? (
                    <View>
                        <Strut size={spacing.medium_16} />
                        <LabelSmall style={styles.error}>
                            {codeError}
                        </LabelSmall>
                        <Strut size={spacing.medium_16} />
                    </View>
                ) : (
                    <Strut size={spacing.large_24} />
                )}
                <Button
                    disabled={classCode.length < 6}
                    onClick={() => {
                        this.handleCodeSubmission(classCode);
                    }}
                >
                    {t`Continue`}
                </Button>
            </View>
        );
    };

    render(): React.ReactNode {
        return (
            <ProgressShellActionScreen
                // If the user is logged in, they probably came here via
                // /coaches. Include a button to help them get back!
                //
                // NOTE(mdr): If we add another major flow, where a different
                //     Back href would make sense, we should make this a prop!
                showBackButton={PermissionsInfo.isLoggedIn}
                backHref="/coaches"
            >
                {this.renderLargeClassCodeInput()}
            </ProgressShellActionScreen>
        );
    }
}

const styles = StyleSheet.create({
    error: {
        color: color.red,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    wrapper: {
        width: "100%",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    backButton: {
        position: "absolute",
        bottom: 0,
        padding: "0 0 19px 16px",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    loginButton: {
        // This height includes an extra 64 pixels for bottom padding.
        // It's wrapped up in the height so it will collapse with the height
        // of the element itself when removed.
        height: 128,
        opacity: 1,
        transition: `
            opacity ${TRANSITION_LENGTH}s ${standardTransitionExtras},
            height ${TRANSITION_LENGTH}s ${standardTransitionExtras}
        `,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    hideLoginButton: {
        height: 0,
        opacity: 0,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    removeLoginButton: {
        display: "none",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    noAnimation: {
        transitionDuration: "0s",
    },
});

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof JoinWidgetActionScreen,
        React.ComponentProps<typeof JoinWidgetActionScreen>
    >
>;
export default withApollo(
    JoinWidgetActionScreen,
) as React.ComponentType<ExportProps>;
